import React, { useEffect, useRef, useState } from "react";
import { ChatScoreCards } from "./chatAnalyticsService";
import { KPI_GRADIENTS } from "./chatAnalyticsPalette";

interface ChatAnalyticsScoreCardsProps {
  data: ChatScoreCards | null;
  loading: boolean;
}

const ChatAnalyticsScoreCards: React.FC<ChatAnalyticsScoreCardsProps> = ({
  data,
  loading,
}) => {
  const stripRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const updateScrollState = () => {
    const el = stripRef.current;
    if (!el) return;
    setCanScrollPrev(el.scrollLeft > 4);
    setCanScrollNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    setIsOverflowing(el.scrollWidth > el.clientWidth + 1);
  };

  useEffect(() => {
    updateScrollState();
    const el = stripRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [data, loading]);

  const scrollByPage = (direction: 1 | -1) => {
    const el = stripRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.85, behavior: "smooth" });
  };

  if (loading || !data) {
    return (
      <div className="ca-kpi-carousel">
        <div className="ca-kpi-strip">
          {[0, 1, 2, 3, 4].map((key) => (
            <div className="ca-kpi-card" key={key}>
              <div className="ca-skeleton mb-3" style={{ width: 44, height: 44, borderRadius: 13 }} />
              <div className="ca-skeleton mb-2" style={{ width: "60%", height: 12 }} />
              <div className="ca-skeleton" style={{ width: "40%", height: 24 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const cards = [
    {
      icon: "ti ti-message-circle",
      label: "Total Chats",
      value: (data.total_chats?.external || 0) + (data.total_chats?.internal || 0),
      sub: `${data.total_chats?.external || 0} external · ${data.total_chats?.internal || 0} internal`,
    },
    {
      icon: "ti ti-messages",
      label: "Total Messages",
      value: (data.total_messages?.external || 0) + (data.total_messages?.internal || 0),
      sub: `${data.total_messages?.external || 0} external · ${data.total_messages?.internal || 0} internal`,
    },
    {
      icon: "ti ti-chart-pie",
      label: "External Chat Messages",
      value: (data.external_chat_message_evaluation?.total_customer_messages || 0) + (data.external_chat_message_evaluation?.total_agent_replies || 0) + (data.external_chat_message_evaluation?.total_bot_replies || 0),
      sub: `${data.external_chat_message_evaluation?.total_agent_replies || 0} agent · ${data.external_chat_message_evaluation?.total_customer_messages || 0} customer · ${data.external_chat_message_evaluation?.total_bot_replies || 0} bot replies`,
    },
    {
      icon: "ti ti-chart-bar",
      label: "Internal Chat Messages",
      value: (data.internal_chat_message_evaluation?.total_sent_messages || 0) + (data.internal_chat_message_evaluation?.total_received_messages || 0),
      sub: `${data.internal_chat_message_evaluation?.total_sent_messages || 0} sent messages · ${data.internal_chat_message_evaluation?.total_received_messages || 0} received messages`,
    },
    {
      icon: "ti ti-plug-connected",
      label: "Total Bots Connected",
      value: (data.total_connected_bots?.external || 0) + (data.total_connected_bots?.internal || 0),
      sub: `${data.total_connected_bots?.external || 0} external · ${data.total_connected_bots?.internal || 0} internal`,
    },
  ];

  return (
    <div className="ca-kpi-carousel">
      <button
        type="button"
        className="ca-kpi-nav ca-kpi-nav-prev"
        onClick={() => scrollByPage(-1)}
        disabled={!canScrollPrev}
        aria-label="Show previous score cards"
      >
        <i className="ti ti-chevron-left" />
      </button>

      <div
        className={`ca-kpi-strip ${!isOverflowing ? "ca-kpi-strip--centered" : ""}`}
        ref={stripRef}
      >
        {cards.map((card, index) => {
          const accent = KPI_GRADIENTS[index % KPI_GRADIENTS.length];
          return (
            <div
              className="ca-kpi-card"
              key={card.label}
              style={
                {
                  "--ca-accent": accent.base,
                  "--ca-accent-dark": accent.dark,
                  minWidth: "280px"
                } as React.CSSProperties
              }
            >
              <span className="ca-kpi-glow" />
              <div className="d-flex align-items-start justify-content-between mb-1">
                <p className="ca-kpi-label mb-0">{card.label}</p>
                <span className="ca-kpi-icon">
                  <i className={card.icon} />
                </span>
              </div>
              <h2 className="ca-kpi-value num">{card.value.toLocaleString()}</h2>
              <p className="ca-kpi-sub mb-0" title={card.sub}>
                {card.sub}
              </p>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="ca-kpi-nav ca-kpi-nav-next"
        onClick={() => scrollByPage(1)}
        disabled={!canScrollNext}
        aria-label="Show more score cards"
      >
        <i className="ti ti-chevron-right" />
      </button>
    </div>
  );
};

export default ChatAnalyticsScoreCards;
